/**
 * DriverPaymentPanel — driver-side capture of payout / bank details.
 * Pure UI + a simple GET/PUT against /driver/payment-details. No dispatch,
 * fare, or payment-processing logic is touched — this is a payout reference
 * record only. Light UK validation mirrors the backend (sort code 6 digits,
 * account number 8 digits) with friendly inline messages.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Landmark, CheckCircle2, Loader2 } from "lucide-react";

const EMPTY = {
  account_holder_name: "",
  bank_name: "",
  sort_code: "",
  account_number: "",
  company_name: "",
  utr: "",
};

const onlyDigits = (s) => (s || "").replace(/\D/g, "");

export const DriverPaymentPanel = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/driver/payment-details");
      const d = res.data || {};
      // Coerce any null string fields to "" so React inputs stay controlled
      // and .trim() in save() never blows up on a null payload field.
      const coerced = Object.fromEntries(
        Object.keys(EMPTY).map((k) => [k, d[k] == null ? "" : d[k]])
      );
      setForm({ ...EMPTY, ...coerced });
      setUpdatedAt(d.updated_at || null);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load payment details");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.account_holder_name.trim()) {
      toast.error("Account holder name is required.");
      return;
    }
    const sortDigits = onlyDigits(form.sort_code);
    if (form.sort_code.trim() && sortDigits.length !== 6) {
      toast.error("Sort code must be 6 digits (e.g. 12-34-56).");
      return;
    }
    const acctDigits = onlyDigits(form.account_number);
    if (form.account_number.trim() && acctDigits.length !== 8) {
      toast.error("Account number must be 8 digits.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/driver/payment-details", {
        account_holder_name: form.account_holder_name.trim(),
        bank_name: form.bank_name.trim() || null,
        sort_code: form.sort_code.trim() || null,
        account_number: form.account_number.trim() || null,
        company_name: form.company_name.trim() || null,
        utr: form.utr.trim() || null,
      });
      const d = res.data || {};
      const coerced = Object.fromEntries(
        Object.keys(EMPTY).map((k) => [k, d[k] == null ? "" : d[k]])
      );
      setForm({ ...EMPTY, ...coerced });
      setUpdatedAt(d.updated_at || null);
      toast.success("Payment details saved.");
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save payment details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="driver-payment-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-black tracking-tight flex items-center gap-2">
            <Landmark className="w-4 h-4" /> Payment details
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Where we send your payouts. Account holder name is required.
          </p>
        </div>
        {updatedAt && (
          <span className="label-mini text-[#065F46] inline-flex items-center gap-1" data-testid="payment-saved-badge">
            <CheckCircle2 className="w-3 h-3" /> ON FILE
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="label-mini">ACCOUNT HOLDER NAME *</Label>
            <Input
              value={form.account_holder_name}
              onChange={(e) => set("account_holder_name", e.target.value)}
              placeholder="As it appears on the account"
              maxLength={120}
              data-testid="payment-account-holder"
            />
          </div>
          <div>
            <Label className="label-mini">BANK NAME</Label>
            <Input
              value={form.bank_name}
              onChange={(e) => set("bank_name", e.target.value)}
              placeholder="e.g. Barclays"
              maxLength={120}
              data-testid="payment-bank-name"
            />
          </div>
          <div>
            <Label className="label-mini">SORT CODE</Label>
            <Input
              value={form.sort_code}
              onChange={(e) => set("sort_code", e.target.value)}
              placeholder="12-34-56"
              maxLength={12}
              data-testid="payment-sort-code"
            />
          </div>
          <div>
            <Label className="label-mini">ACCOUNT NUMBER</Label>
            <Input
              value={form.account_number}
              onChange={(e) => set("account_number", e.target.value)}
              placeholder="8 digits"
              maxLength={20}
              data-testid="payment-account-number"
            />
          </div>
          <div>
            <Label className="label-mini">COMPANY NAME (OPTIONAL)</Label>
            <Input
              value={form.company_name}
              onChange={(e) => set("company_name", e.target.value)}
              placeholder="If paid via a limited company"
              maxLength={200}
              data-testid="payment-company-name"
            />
          </div>
          <div>
            <Label className="label-mini">UTR (OPTIONAL)</Label>
            <Input
              value={form.utr}
              onChange={(e) => set("utr", e.target.value)}
              placeholder="Unique Taxpayer Reference"
              maxLength={30}
              data-testid="payment-utr"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="payment-save-btn">
              {saving ? "Saving…" : "Save payment details"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverPaymentPanel;
